import { takeLatest, put, call, select } from 'redux-saga/effects';

import {
  LANGUAGE,
  WHITE_LABEL_AFFILIATE,
  getItemFromLocalStorage,
  getPendingLanguageFromSession,
  removePendingLanguageFromSession
} from 'shared/utils/local-storage';
import { IAffiliate } from 'shared/interfaces/affiliate.interface';
import { resolveLanguage } from 'shared/helpers/language';
import { changeLanguage } from 'shared/helpers/changeLanguage';
import { getOVSAffiliateSelector } from 'store/onlineVisit/onlineVisit.redux';
import {
  IUpdateUserLanguageRequest,
  IUser
} from 'shared/interfaces/user.interface';
import UserService from 'shared/services/UserService';
import AffiliateService from 'shared/services/AffiliateService';
import {
  getUserData,
  setUserData,
  getUserFail,
  updateUserLanguageData
} from './user.redux';
import { setWhiteLabelAffiliate } from 'store/app/app.redux';
import { TAction } from 'shared/types';
import { setSentryUser } from 'shared/utils/sentry';

function* getLanguageAffiliate() {
  const affiliate: IAffiliate | null = yield select(getOVSAffiliateSelector);
  const affiliateId = getItemFromLocalStorage(WHITE_LABEL_AFFILIATE);
  if (affiliate || !affiliateId) return affiliate;
  try {
    const loaded: IAffiliate = yield call(AffiliateService.getAffiliate, {
      affiliateId
    });
    return loaded;
  } catch (error) {
    return null;
  }
}

function applyUserLanguage(
  userLanguage: string | null | undefined,
  affiliate: IAffiliate | null
) {
  const storedLanguage = getItemFromLocalStorage(LANGUAGE);
  const language = resolveLanguage({
    userLanguage,
    storedLanguage,
    affiliateLanguage: affiliate?.appLanguage,
    supportedLanguages: affiliate?.supportedLanguages
  });
  if (language !== storedLanguage) {
    changeLanguage(language);
  }
  return language;
}

function* getUser() {
  try {
    const whiteLabelAffiliate = getItemFromLocalStorage(WHITE_LABEL_AFFILIATE);
    const result: IUser = yield call(
      UserService.getUser,
      whiteLabelAffiliate || undefined
    );

    const pendingLanguage = getPendingLanguageFromSession();
    const languageAffiliate: IAffiliate | null = yield call(
      getLanguageAffiliate
    );
    const canResolvePending =
      !!pendingLanguage && (!whiteLabelAffiliate || !!languageAffiliate);
    const language: string | undefined = result
      ? yield call(
          applyUserLanguage,
          canResolvePending ? pendingLanguage : result.userLanguage,
          languageAffiliate
        )
      : undefined;
    const pendingAccepted = canResolvePending && language === pendingLanguage;

    yield put(
      setUserData(
        result && pendingAccepted
          ? { ...result, userLanguage: language }
          : result
      )
    );

    if (result && canResolvePending) {
      if (pendingAccepted && result.userLanguage !== language) {
        yield put(
          updateUserLanguageData({ userId: result.id, userLanguage: language })
        );
      } else {
        removePendingLanguageFromSession();
      }
    }

    // Set Sentry user context with patient ID
    if (result?.patient?.id) {
      setSentryUser(result.patient.id);
    }

    if (result && whiteLabelAffiliate) {
      const affiliate = result.patient?.affiliates.find(
        (affiliate) => affiliate.id === whiteLabelAffiliate
      );
      if (affiliate) {
        yield put(setWhiteLabelAffiliate(affiliate));
      }
    }
  } catch (error) {
    yield put(getUserFail());
    console.log('getUser error', error);
  }
}

function* updateUserLanguage({ payload }: TAction<IUpdateUserLanguageRequest>) {
  try {
    const result: IUser = yield call(
      UserService.updateUserLanguage,
      payload as IUpdateUserLanguageRequest
    );
    if (getPendingLanguageFromSession() === payload?.userLanguage) {
      removePendingLanguageFromSession();
    }
  } catch (error) {
    yield put(getUserFail());
    console.log('Update user language error', error);
  }
}

function* User() {
  yield takeLatest(getUserData.type, getUser);
  yield takeLatest(updateUserLanguageData.type, updateUserLanguage);
}

export default User;
